/**
 * Данные рабочего дня: типы, запросы, мутации.
 *
 * Экран «Мой день» — самый горячий в приложении, поэтому здесь же живут
 * оптимистичные обновления: событие должно появляться мгновенно, а сеть
 * догоняет потом (17.2E).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query'
import { useRef } from 'react'

import { ApiError, api, newIdempotencyKey } from '@/shared/api/client'
import { POLL_INTERVAL_MS, queryKeys } from '@/shared/api/queries'
import type { MoneyString } from '@/shared/api/types'

export type ShiftStatus = 'planned' | 'active' | 'finished'
export type ExpenseStatus = 'pending' | 'approved' | 'rejected' | 'reimbursed'
export type ExpenseCategory =
  | 'food'
  | 'transport'
  | 'activity'
  | 'pharmacy'
  | 'household'
  | 'other'

export interface GeoPoint {
  lat: string
  lon: string
  accuracy_m?: number | null
  captured_at?: string | null
  source_action?: string | null
  place_id?: number | null
  place_title?: string | null
  is_approximate: boolean
}

export interface EventType {
  id: number
  code: string
  emoji: string
  title: string
  requires_child: boolean
  is_interval: boolean
  capture_geo: boolean
  sort: number
}

export interface TimelineEvent {
  id: number
  type_id: number
  type_code: string
  emoji: string
  title: string
  occurred_at: string
  ended_at?: string | null
  is_interval: boolean
  child_id?: number | null
  note?: string | null
  created_by: number
  shift_id?: number | null
  photo_urls: string[]
  thumb_urls: string[]
  geo?: GeoPoint | null
}

export interface Shift {
  id: number
  status: ShiftStatus
  nanny_id: number
  actual_start?: string | null
  actual_end?: string | null
  start_source?: string | null
  end_source?: string | null
  minutes_total?: number | null
  minutes_overtime?: number | null
  amount?: MoneyString | null
  currency: string
  note?: string | null
  edited_at?: string | null
  is_locked: boolean
  start_geo?: GeoPoint | null
  end_geo?: GeoPoint | null
}

export interface ExpenseCard {
  id: number
  category: ExpenseCategory
  title: string
  amount?: MoneyString | null
  currency: string
  status: ExpenseStatus
  occurred_at: string
  note?: string | null
  reject_reason?: string | null
  auto_approved?: boolean | null
  receipt_urls: string[]
}

export interface DayData {
  date: string
  timezone: string
  shifts: Shift[]
  events: TimelineEvent[]
  expenses: ExpenseCard[]
  active_shift?: Shift | null
  active_intervals: TimelineEvent[]
  minutes_so_far?: number | null
  amount_so_far?: MoneyString | null
  geo_active: boolean
}

export interface GeoStatus {
  geo_enabled: boolean
  consent_granted: boolean
  consent_at?: string | null
  last_capture_at?: string | null
  last_capture_action?: string | null
  last_capture_accuracy_m?: number | null
  last_capture_place?: string | null
  visible_to: string[]
}

/* ─── Запросы ───────────────────────────────────────────────────────────── */

export function useDay(familyId: number, date?: string, live = true) {
  const key = date ?? 'today'
  return useQuery<DayData, ApiError>({
    queryKey: queryKeys.day(familyId, key),
    queryFn: () => api.get<DayData>('/day', { query: { date } }),
    // 12 секунд — компромисс из плана: родитель видит событие почти сразу,
    // но телефон няни не греется от запросов
    refetchInterval: live ? POLL_INTERVAL_MS : false,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  })
}

export function useEventTypes(familyId: number) {
  return useQuery<EventType[], ApiError>({
    queryKey: ['event-types', familyId],
    queryFn: () => api.get<EventType[]>('/event-types'),
    staleTime: 10 * 60_000,
  })
}

export function useGeoStatus(familyId: number, enabled = true) {
  return useQuery<GeoStatus, ApiError>({
    queryKey: ['geo-status', familyId],
    queryFn: () => api.get<GeoStatus>('/geo/status'),
    enabled,
    staleTime: 30_000,
  })
}

/* ─── Мутации ───────────────────────────────────────────────────────────── */

function useKey(): () => string {
  const ref = useRef<string>()
  return () => {
    if (!ref.current) ref.current = newIdempotencyKey()
    return ref.current
  }
}

export interface GeoInput {
  lat: string
  lon: string
  accuracy_m?: number
  captured_at?: string
}

export function useStartShift(
  familyId: number,
  options?: UseMutationOptions<Shift, ApiError, GeoInput | undefined>,
) {
  const queryClient = useQueryClient()
  const key = useKey()
  return useMutation<Shift, ApiError, GeoInput | undefined>({
    mutationFn: (geo) => api.post<Shift>('/shifts/start', { geo }, { idempotencyKey: key() }),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface FinishShiftInput {
  shiftId: number
  note?: string
  geo?: GeoInput
}

export function useFinishShift(
  familyId: number,
  options?: UseMutationOptions<Shift, ApiError, FinishShiftInput>,
) {
  const queryClient = useQueryClient()
  const key = useKey()
  return useMutation<Shift, ApiError, FinishShiftInput>({
    mutationFn: ({ shiftId, ...body }) =>
      api.post<Shift>(`/shifts/${shiftId}/finish`, body, { idempotencyKey: key() }),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface CreateEventInput {
  type_id?: number
  type_code?: string
  child_id?: number
  note?: string
  media_ids?: number[]
  geo?: GeoInput
}

export function useCreateEvent(
  familyId: number,
  options?: UseMutationOptions<TimelineEvent, ApiError, CreateEventInput>,
) {
  const queryClient = useQueryClient()
  return useMutation<TimelineEvent, ApiError, CreateEventInput>({
    // ключ на каждый тап свой: два «покормила» подряд — это два события,
    // а не повтор одного
    mutationFn: (input) => api.post<TimelineEvent>('/events', input),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useStopInterval(
  familyId: number,
  options?: UseMutationOptions<TimelineEvent, ApiError, number>,
) {
  const queryClient = useQueryClient()
  return useMutation<TimelineEvent, ApiError, number>({
    mutationFn: (eventId) => api.post<TimelineEvent>(`/events/${eventId}/stop`),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useDeleteEvent(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, number>,
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, number>({
    mutationFn: (eventId) => api.delete(`/events/${eventId}`),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useHere(
  familyId: number,
  options?: UseMutationOptions<TimelineEvent, ApiError, GeoInput>,
) {
  const queryClient = useQueryClient()
  return useMutation<TimelineEvent, ApiError, GeoInput>({
    mutationFn: (geo) => api.post<TimelineEvent>('/here', { geo }),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export interface CreateExpenseInput {
  category: ExpenseCategory
  amount: string
  title?: string
  note?: string
  media_ids?: number[]
}

export function useCreateExpense(
  familyId: number,
  options?: UseMutationOptions<ExpenseCard, ApiError, CreateExpenseInput>,
) {
  const queryClient = useQueryClient()
  return useMutation<ExpenseCard, ApiError, CreateExpenseInput>({
    mutationFn: (input) => api.post<ExpenseCard>('/expenses', input),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      void queryClient.invalidateQueries({ queryKey: queryKeys.expenses(familyId) })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useApproveExpense(
  familyId: number,
  options?: UseMutationOptions<ExpenseCard, ApiError, number>,
) {
  const queryClient = useQueryClient()
  return useMutation<ExpenseCard, ApiError, number>({
    mutationFn: (id) => api.post<ExpenseCard>(`/expenses/${id}/approve`),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useRejectExpense(
  familyId: number,
  options?: UseMutationOptions<ExpenseCard, ApiError, { id: number; reason: string }>,
) {
  const queryClient = useQueryClient()
  return useMutation<ExpenseCard, ApiError, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) => api.post<ExpenseCard>(`/expenses/${id}/reject`, { reason }),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.day(familyId, 'today') })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useGrantGeoConsent(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, void>,
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => api.post('/geo/consent', { source: 'onboarding' }),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['geo-status', familyId] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useRevokeGeoConsent(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, void>,
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, void>({
    mutationFn: () => api.delete('/geo/consent'),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['geo-status', familyId] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.me })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}
